import {h, Fragment} from "preact"
import { SerpElement, useCurrencyFormatting } from "@nosto/preact"
import { useEffect, useState, useMemo } from "preact/hooks";
import { productImagePlaceholder, imageResizer } from "../helpers"

export default function Product({ product }) {
    const { format } = useCurrencyFormatting()
    const [selectedVariantId, setSelectedVariantId] = useState(product.skus && product.skus.length > 0 ? product.skus[0].id : null)
    const [badgeHtml, setBadgeHtml] = useState(null)
    const [promotionalLineHtml, setPromotionalLineHtml] = useState(null)

    const swatches = [];
    let seenColors = [];
    let hiddenColorsField = product.customFields.filter((obj) => obj.key == "custom_fields-hidden_variants");
    let hiddenColors = hiddenColorsField.length > 0 ? hiddenColorsField[0].value.split(",") : [];
    let swatchCount = 0;
    let altImg = "";

    product.skus?.forEach(el => { 
        el.customFields.forEach(field => {
            if(field.key == "color" && !seenColors.includes(field.value) && !hiddenColors.includes(field.value)) {
                seenColors.push(field.value);
                product.alternateImageUrls?.some((img, idx) => {
                    if(img == el.imageUrl) {
                        altImg = product.alternateImageUrls[idx + 1];
                        return true;
                    }
                    altImg = product.alternateImageUrls[0];
                })
                swatches.push({color: field.value, image: el.imageUrl, altImg: altImg, price: format(el.price).toString().replace(".00", ""), listPrice: format(el.listPrice).toString().replace(".00", ""), id: el.id})
            }
        })
    })

    const handleSwatchClick = (swatchInfo, event) => {
        event.preventDefault();
        event.stopPropagation();
        
        const skuId = swatchInfo.id;
        setSelectedVariantId(skuId);
    }
    
    useEffect(() => {
        if (!selectedVariantId || swatches.length == 1) return;
        
        const selectedSwatch = swatches.find(swatch => swatch.id === selectedVariantId);
        if (!selectedSwatch) return;
        
        const productElement = document.querySelector(`.ns-product[href*="${product.url.split('?')[0]}"]`);
        if (!productElement) return;
        
        const selectedText = productElement.querySelector('[data-product-option-group-selected-text]');
        if (selectedText) {
            selectedText.innerText = selectedSwatch.color;
        }
        
        const mainImage = productElement.querySelector('img.main-image');
        if (mainImage && selectedSwatch.image) {
            mainImage.src = imageResizer(selectedSwatch.image, 400);
        }
        
        const secondaryImage = productElement.querySelector('img.secondary-image');
        if (secondaryImage && selectedSwatch.altImg) {
            secondaryImage.src = imageResizer(selectedSwatch.altImg, 400);
        }
        
        if (productElement.href) {
            productElement.href = productElement.href.split('?')[0] + '?variant=' + selectedVariantId;
        }
    }, [selectedVariantId, swatches, product.url])


    useEffect(() => {
        if (selectedVariantId) {
            fetch(`${product.url}?variant=${selectedVariantId}&sections[]=badges-section-api&sections[]=promotional-line-section-api`)
                .then(response => response.json())
                .then(data => {
                    const parser = new DOMParser();
                    const badgeDoc = parser.parseFromString(data["badges-section-api"], 'text/html');
                    const badgeContent = badgeDoc.querySelector('[data-badge]');

                    const promotionalLineDoc = parser.parseFromString(data["promotional-line-section-api"], 'text/html');
                    const promotionalLineContent = promotionalLineDoc.querySelector('[data-promotional-line]');
                    if (promotionalLineContent) {
                        setPromotionalLineHtml(promotionalLineContent.outerHTML);
                    } else {
                        setPromotionalLineHtml(null);
                    }

                    if (badgeContent) {
                        setBadgeHtml(badgeContent.outerHTML);
                    } else {
                        setBadgeHtml(null);
                    }
                })
                .catch(error => {
                    console.error('Error fetching section HTML:', error);
                });
        }
    }, [selectedVariantId]);

    const swatchesField = useMemo(() => product.customFields?.find(f => f.key === 'custom-combined_options')?.value, [])
    
    const handleQuickAdd = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if(typeof window.nostoQuickAdd !== 'function') return;
        window.nostoQuickAdd(product.productId, null, swatchesField, 'serp');
    }

    return (
        <SerpElement
            as="a"
            hit={product}
            class="ns-product ns-border-box ns-clickable ns-text-undecorated ns-color-inherit ns-col-12 ns-col-xs-6 ns-col-l-3 ns-mx-0 product-card"
        >
            <div class="product-card--images">
                {badgeHtml && <div class="ns-product__badge" dangerouslySetInnerHTML={{ __html: badgeHtml }} />}
                <img
                    class="ns-w-100 ns-h-auto main-image"
                    main-img={product.imageUrl}
                    src={imageResizer(product.imageUrl, 400) ?? productImagePlaceholder}
                    alt={product.name}
                    width="400"
                    height="400"
                />
                {product.alternateImageUrls && product.alternateImageUrls.length > 0 && (
                    <img
                        class="ns-w-100 ns-h-auto secondary-image"
                        src={imageResizer(product.alternateImageUrls[0], 400) ?? productImagePlaceholder}
                        alt={product.name}
                        width="400"
                        height="400"
                    />
                )}
                {/* <button class="ns-quickadd-toggle" type="button" aria-haspopup="dialog" aria-controls="nosto-quickadd" onClick={handleQuickAdd}>
                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg" role="presentation" aria-hidden="true">
                        <path d="M4 0V8" stroke="#222222" stroke-width="1.5"/><path d="M0 4L8 4" stroke="#222222" stroke-width="1.5"/>
                    </svg>
                    <span>Quick Add</span>
                </button> */}
            </div>
            <div class="ns-relative ns-mt-4 product-card--content" data-nosto-element="product">
                <div className="product-card--details-wrapper">
                    <div class="ns-clipped ns-text-four-lines ns-text-md-three-lines ns-font-4 product__title heading-6">{product.name}</div>
                    <div class="product__meta">
                        {product.listPrice && product.listPrice > product.price && (
                            <>
                                <span class="sale-price" data-collection-item-sale-price="">{format(product.price).toString().replace(".00", "")}</span>
                                <s data-collection-item-compare-price="">{format(product.listPrice).toString().replace(".00", "")}</s>
                            </>
                        )}
                        {!product.listPrice || product.listPrice == product.price && (
                            <p class="collection-item__price ns-price" data-collection-item-price="">
                                <span data-collection-item-sale-price="">{format(product.price).toString().replace(".00", "")}</span>
                            </p>
                        )}
                    </div>
                    {promotionalLineHtml && <div className="ns-product__promo-line" dangerouslySetInnerHTML={{__html: promotionalLineHtml }}/>}
                </div>
                {swatches?.length > 0 && (
                    <div class="product-options">
                        <fieldset class="product-option" data-product-option-group="" data-option-name="color" data-option-index="1" data-type="swatch">
                            <legend>
                                <strong>Color: </strong><span data-product-option-group-selected-text="">{swatches[0].color}</span>
                            </legend>
                            <div class="product-option__option-values">
                                {
                                    swatches?.map((sku, idx) => {
                                        let jointColorName = sku.color.toString().toLowerCase().split(" ").join("-");
                                        let productHandle = product.url.toString().toLowerCase().split("/products/")[1];
                                        let swatchImg = "//southerntide.com/cdn/shop/files/" + productHandle + "-" + jointColorName + "_40x.jpg";
                                        if(idx === 0) {
                                            return(
                                                <div class="product-option-item product-option-item--color" data-product-option-item="" data-first-variant onClick={(e) => handleSwatchClick(sku, e)} data-swatch={JSON.stringify(sku)}>
                                                    <input type="radio" id={`product-swatch-template--${product.productId}-${sku.id}-${jointColorName}`} value={sku.color} 
                                                        name={`product-swatch-template--${product.productId}-color`}
                                                        checked={selectedVariantId === sku.id}
                                                        data-product-option="" data-available="true" data-variant-id={sku.id} data-option-index="1" 
                                                        data-type="radio" data-current-price={sku.price} data-compare-price={sku.listPrice} data-selected-text={sku.color} />
                                                    <label for={`product-swatch-template--${product.productId}-${sku.id}-${jointColorName}`}>
                                                        <div class="product-option-item__swatch" style={`background-image:url(${swatchImg});`}></div>
                                                        <span class="visually-hidden">{sku.color}</span>
                                                    </label>
                                                </div>
                                            )
                                        } else if(idx <= 5) {
                                            return(
                                                <div class="product-option-item product-option-item--color" data-product-option-item="" onClick={(e) => handleSwatchClick(sku, e)} data-swatch={JSON.stringify(sku)}>
                                                    <input type="radio" id={`product-swatch-template--${product.productId}-${sku.id}-${jointColorName}`} value={sku.color} 
                                                        name={`product-swatch-template--${product.productId}-color`}
                                                        checked={selectedVariantId === sku.id}
                                                        data-product-option="" data-available="true" data-variant-id={sku.id} data-option-index="1" 
                                                        data-type="radio" data-current-price={sku.price} data-compare-price={sku.listPrice} data-selected-text={sku.color} />
                                                    <label for={`product-swatch-template--${product.productId}-${sku.id}-${jointColorName}`}>
                                                        <div class="product-option-item__swatch" style={`background-image:url(${swatchImg});`}></div>
                                                        <span class="visually-hidden">{sku.color}</span>
                                                    </label>
                                                </div>
                                            )
                                        } else {
                                            swatchCount = swatchCount + 1
                                            return(
                                                <div style="display:none;" class="product-option-item product-option-item--color" data-product-option-item="" onClick={(e) => handleSwatchClick(sku, e)} data-swatch={JSON.stringify(sku)}>
                                                    <input type="radio" id={`product-swatch-template--${product.productId}-${sku.id}-${jointColorName}`} value={sku.color} 
                                                        name={`product-swatch-template--${product.productId}-color`}
                                                        checked={selectedVariantId === sku.id}
                                                        data-product-option="" data-available="true" data-variant-id={sku.id} data-option-index="1" 
                                                        data-type="radio" data-current-price={sku.price} data-compare-price={sku.listPrice} data-selected-text={sku.color} />
                                                    <label for={`product-swatch-template--${product.productId}-${sku.id}-${jointColorName}`}>
                                                        <div class="product-option-item__swatch" style={`background-image:url(${swatchImg});`}></div>
                                                        <span class="visually-hidden">{sku.color}</span>
                                                    </label>
                                                </div>
                                            )
                                        }
                                    })
                                }
                                { swatchCount > 0 && (
                                    <>
                                        <div class="showMoreSwatches desktop">+ {swatchCount} </div>
                                        <div class="showMoreSwatches mobile">+ {swatchCount + 3} </div>
                                    </>
                                )}
                            </div>
                        </fieldset>
                    </div>
                )}
            </div>
        </SerpElement>
    )
}
